// Spur kontingent, Gerüst der Oberfläche (SPEC-KONTINGENT.md 3.4; welle-2/kontingent/AUFTEILUNG.md 2.2, S3-0): die Regeln aus
// 3.4, die die Oberfläche live weiterrechnet, als reine Funktionen ein zweites Mal – wie am Server in
// backend/src/administration/billing/prozent.ts (KO-01). Jede Zahl, die der Server liefert, ist schon gerundet; hier wird nur
// mit gerundeten Eingängen weitergerechnet, ganzzahlig in Zehntelprozent, Cent und Tausendsteln, ohne Gleitkommareste.
// Geprüft in frontend/tests/kontingent-prozent.test.mjs gegen die Fälle des Servers
// (backend/tests/administration/fixtures/kontingent-prozent-faelle.json).

const zahl = (wert: number, stellen = 1) => wert.toLocaleString('de-AT', { maximumFractionDigits: stellen });

/** Zehntelprozent einer schon gerundeten Zahl mit höchstens einer Nachkommastelle (16,7 → 167). */
const zehntel = (prozent: number) => Math.round(prozent * 10);

/**
 * Eingabe eines Werts in Prozent (3.4, Runde 2): angenommen werden „5“, „5 %“, „5%“, „16,7“ und „16.7“; Leerzeichen und das
 * Prozentzeichen fallen weg, Komma wie Punkt. Gültig sind 0,1 bis 100 mit höchstens einer Nachkommastelle; sonst null.
 */
export function prozentEingabe(text: string | number): number | null {
  const wert = zehntelEingabe(text, 3);
  return wert !== null && wert >= 1 && wert <= 1000 ? wert / 10 : null;
}
/** Zehntelprozent einer Eingabe mit höchstens einer Nachkommastelle, ohne Leerzeichen und „%“; sonst null. */
function zehntelEingabe(text: string | number, ganzeStellen: number): number | null {
  const roh = String(text).replace(/[\s  %]/g, '').replace(',', '.');
  return new RegExp(`^\\d{1,${ganzeStellen}}(\\.\\d)?$`).test(roh) ? zehntel(Number(roh)) : null;
}

/** Fehler am Feld bei einer ungültigen Eingabe (KT-37, KT-43). */
export const PROZENT_FEHLER = 'Bitte einen Wert zwischen 0,1 und 100 Prozent eingeben, mit höchstens einer Nachkommastelle.';

/**
 * Eingabe des Inhabers (KT-83, KT-92; Runde 3, RL3-09 und Kleinigkeit 10), wie inhaberProzentBereich am Server: für die
 * ganze Firma 0,1 bis 0,9 mit einer Nachkommastelle oder ganze Zahlen ab 1, damit „Die Firma liest“ der Eingabe gleicht; für
 * eine Person 0,1 bis 100 mit einer Nachkommastelle. Die obere Grenze in Euro (€ 1.000.000,00) prüft der Server.
 */
export function inhaberProzentBereich(art: 'FIRMA' | 'PERSON', text: string | number): number | null {
  const wert = zehntelEingabe(text, 9);
  if (wert === null || wert < 1) return null;
  if (art === 'PERSON') return wert <= 1000 ? wert / 10 : null;
  return wert >= 10 && wert % 10 !== 0 ? null : wert / 10;
}

/**
 * Gleich der Anzeige des eigenen gespeicherten Werts (Runde 2, RL2-05; Runde 3, Kleinigkeit 1): dann bleibt der Cent-Wert,
 * und die Oberfläche schickt das Feld nicht. Ohne eigenen Wert (Vorgabe der Firma) gilt jede Eingabe als Änderung.
 */
export function unveraendert(eingabeProzent: number, gespeichertCent: number | null, kontingentCent: number): boolean {
  return gespeichertCent !== null && centZuProzent(kontingentCent, gespeichertCent) === eingabeProzent;
}

/** Prozent → Cent, auf den Cent aufgerundet (3.4, 3.7): aufgerundet(B × p / 100). 3,1 % von 70 000 Cent → 2 170 Cent. */
export function prozentZuCent(kontingentCent: number, prozent: number): number {
  return Math.ceil(kontingentCent * zehntel(prozent) / 1000);
}

/** Cent → Prozent, auf eine Nachkommastelle abgerundet (3.4). 2 200 Cent von 70 000 Cent → 3,1. */
export function centZuProzent(kontingentCent: number, cent: number): number {
  if (kontingentCent <= 0) return 0;
  return Math.floor(cent * 1000 / kontingentCent) / 10;
}

/**
 * Ein Prozentwert zum Lesen (3.4): ganze Zahlen ohne Komma, sonst eine Nachkommastelle; ein Stand zwischen 0 und 1 heißt
 * „unter 1 %“ (unterEinem), ein Wert unter 0,1 „unter 0,1 %“ (unterZehntel oder ein Wert über 0 und unter 0,1).
 */
export function prozentAnzeige(wert: number, lage: { unterEinem?: boolean; unterZehntel?: boolean } = {}): string {
  if (lage.unterEinem && wert < 1) return 'unter 1 %';
  if (lage.unterZehntel || (wert > 0 && wert < 0.1)) return 'unter 0,1 %';
  return `${zahl(wert)} %`;
}

/** Größe eines Zusatzes für die ganze Firma (3.4, KT-12): unter 0,05 „weniger als 0,1 %“ (groesseUnterZehntel). */
export function groesseAnzeige(groesseProzent: number, groesseUnterZehntel = false): string {
  return groesseUnterZehntel ? 'weniger als 0,1 %' : `${zahl(groesseProzent)} %`;
}

/** platzZuHeute (drei Stellen, aus GET /billing/plans) in ganzen Tausendsteln: 0,429 → 429. */
export const tausendstel = (platzZuHeute: number) => Math.round(platzZuHeute * 1000);

/**
 * Ein Faktor aus Tausendsteln, so gerundet, wie die Seiten ihn zeigen (3.4, Regel unter KT-54): unter 2 auf ganze Prozent
 * Unterschied (kaufmännisch, auch nach unten: 835 → 0,83), ab 2 bis 10 auf halbe, darüber auf ganze. „um 100 % größer“ wird
 * 2. Unter 0,1 auf zwei Stellen abgerundet wie am Server (0,002 → 0; er heißt „weniger als ein Zehntel von heute“,
 * Kleinigkeit 3; ABWEICHUNGEN.md SK-2).
 */
export function faktorRunden(faktorTausendstel: number): number {
  const t = Math.max(0, Math.round(faktorTausendstel));
  if (t < 100) return Math.floor(t / 10) / 100;
  if (t < 2000) {
    const unterschied = Math.floor((Math.abs(t - 1000) + 5) / 10);
    return t >= 1000 ? (100 + unterschied) / 100 : (100 - unterschied) / 100;
  }
  if (t <= 10000) return Math.floor((t + 250) / 500) / 2;
  return Math.floor((t + 500) / 1000);
}

/**
 * Der Faktor einer Platzzahl (Runde 3, RL3-02): platzZuHeute in ganzen Tausendsteln mal Plätze, ganzzahlig wie am Server,
 * dann gerundet. Talblick → Unternehmen mit 4 Plätzen: 429 × 4 = 1 716 → 1,72 („um 72 % größer“); Unterrainer 6 → 5:
 * 167 × 5 = 835 → 0,83 („um 17 % kleiner“).
 */
export function faktorAusPlaetzen(platzZuHeuteTausendstel: number, plaetze: number): number {
  return faktorRunden(Math.round(platzZuHeuteTausendstel) * plaetze);
}

/**
 * Ein gerundeter Faktor in Worten (Regel unter KT-54): unter 2 als Prozent („um 43 % größer als heute“), ab 2 als Vielfaches
 * („rund 3,5-mal so groß wie heute“), gleich („gleich groß wie heute“), unter 0,1 „weniger als ein Zehntel von heute“.
 * bezug: „heute“ bzw. „im Tarif Start“. wort „viel“ für die Sätze über den Preis (KT-52, KT-54: „rund 9-mal so viel wie
 * heute“). In der Tariftabelle (tarifliste) steht unter 1,5 kein Vergleich (null).
 */
export function faktorText(faktor: number, bezug: string, lage: { wort?: 'gross' | 'viel'; tarifliste?: boolean } = {}): string | null {
  const t = Math.round(faktor * 1000);
  if (lage.tarifliste && t < 1500) return null;
  const viel = lage.wort === 'viel';
  if (t < 100) return `weniger als ein Zehntel von ${bezug}`;
  if (t >= 2000) return `rund ${zahl(t / 1000)}-mal so ${viel ? 'viel' : 'groß'} wie ${bezug}`;
  const unterschied = Math.round(Math.abs(t - 1000) / 10);
  if (unterschied === 0) return `${viel ? 'gleich viel' : 'gleich groß'} wie ${bezug}`;
  return `um ${unterschied} % ${t > 1000 ? (viel ? 'mehr' : 'größer') : (viel ? 'weniger' : 'kleiner')} als ${bezug}`;
}

/**
 * Orientierung unter der Auswahl der persönlichen Grenze (KT-37): 100 durch die Platzzahl aus der Herkunft (3.1), ganze Prozent
 * kaufmännisch. „Bei 7 gebuchten Plätzen“ bei der Herkunft TARIF, sonst „Bei 2 Plätzen“; nicht ohne Platzzahl und nicht bei
 * einem Platz (null). Das Einzelkonto ruft die Funktion nicht.
 */
export function gleicherTeil(plaetze: number | null | undefined, gebucht: boolean): string | null {
  if (!plaetze || plaetze <= 1) return null;
  const teil = Math.floor((10000 / plaetze + 50) / 100);
  return `Zur Orientierung: Bei ${zahl(plaetze, 0)} ${gebucht ? 'gebuchten ' : ''}Plätzen wäre ein gleicher Teil für jede Person rund ${zahl(teil, 0)} %.`;
}

/**
 * Erfahrungswert mal Eingabe (KT-43, KT-56): beide mit höchstens einer Nachkommastelle, ganzzahlig in Hundertsteln gerechnet.
 * Ergebnis die Zahl der Recherchen in Hundertsteln (520 = 5,2).
 */
export function recherchenAusErfahrung(recherchenJeProzent: number, prozent: number): number {
  return zehntel(recherchenJeProzent) * zehntel(prozent);
}

/**
 * „rund 5 weitere Recherchen“ (KT-43) bzw. „rund 52 Recherchen“ (KT-56) aus Hundertsteln, kaufmännisch gerundet; genau eine
 * „rund eine weitere Recherche“, unter 0,5 „weniger als eine weitere Recherche“ (3.4).
 */
export function rundRecherchen(hundertstel: number, weitere = true): string {
  const w = weitere ? 'weitere ' : '';
  if (hundertstel < 50) return `weniger als eine ${w}Recherche`;
  const anzahl = Math.floor((hundertstel + 50) / 100);
  return anzahl === 1 ? `rund eine ${w}Recherche` : `rund ${zahl(anzahl, 0)} ${w}Recherchen`;
}

/**
 * Ergebnis im Fenster „Mehr für {Name}“ (KT-43; Runde 3, RL3-11): die Grenze mit Zusatz vom Server (eine Nachkommastelle) plus
 * die Eingabe, in Zehntelprozent addiert. 4,7 + 1 → 5,7.
 */
export function grenzeDanach(grenzeMitZusatzProzent: number, eingabe: number): number {
  return (zehntel(grenzeMitZusatzProzent) + zehntel(eingabe)) / 10;
}

/**
 * Eurowert neben einer Eingabe des Inhabers in Prozent (KT-82, KT-83, KT-92): der Cent-Wert, den der Server speichert
 * (aufgerundet), aus dem ungerundeten Kontingent der Firma (kontingentCents aus KO-03). Nur für den Inhaber (siehtEuro).
 */
export const prozentZuEuroCent = (kontingentCent: number, prozent: number) => prozentZuCent(kontingentCent, prozent);
